import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Network, Users, Cloud, Server, CheckCircle2 } from "lucide-react";

/**
 * V14 D4 — Hybrid Coordination Rollup panel.
 *
 * Groups `recovery_tasks` with `task_type=hybrid_recovery_coordination` by
 * their shared `coordination_group_id` so responders see one workstream card
 * per hybrid application (instead of 3 disconnected rows for IT Function /
 * IT-DRP / App Manager).
 *
 * V14 P0 — per-tile status update control wired to
 * `PATCH /api/ai-v14/orchestration/hybrid-tasks/{task_id}/status` which also
 * recomputes the group's completion percentage.
 */
const STATUS_OPTIONS = ["pending", "in_progress", "completed", "blocked"];

export default function HybridCoordinationRollup() {
  const { api, user } = useAuth();
  const [data, setData] = useState({ count: 0, groups: [] });
  const [loading, setLoading] = useState(true);
  const [savingTaskId, setSavingTaskId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/ai-v14/orchestration/hybrid-coordination-groups");
      setData(r.data || { count: 0, groups: [] });
    } catch {
      // silent — widget hidden when no data / no permission
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || data.count === 0) return null;

  const roleIcon = (role) => {
    if (role === "it_function_manager") return Server;
    if (role === "it_drp_manager") return Network;
    return Cloud; // app_manager / devops
  };
  const roleTone = (role) => {
    if (role === "it_function_manager") return "bg-indigo-100 text-indigo-700 border-indigo-200";
    if (role === "it_drp_manager") return "bg-purple-100 text-purple-700 border-purple-200";
    return "bg-emerald-100 text-emerald-700 border-emerald-200";
  };

  const canEdit = (task) => {
    const r = user?.role;
    if (!r) return false;
    if (r === "admin" || r === "bcm_manager" || r === "it_drp_manager") return true;
    return r === task.assigned_role;
  };

  const updateStatus = async (task, groupId, newStatus) => {
    if (newStatus === task.status) return;
    setSavingTaskId(task.id);
    try {
      const r = await api.patch(
        `/ai-v14/orchestration/hybrid-tasks/${task.id}/status`,
        { status: newStatus },
      );
      // optimistic local update
      setData((prev) => {
        const groups = (prev.groups || []).map((g) => {
          if (g.coordination_group_id !== groupId) return g;
          const tasks = (g.tasks || []).map((t) =>
            t.id === task.id ? { ...t, status: newStatus } : t,
          );
          return {
            ...g,
            tasks,
            completion_pct:
              r.data?.group_summary?.completion_pct ?? g.completion_pct,
          };
        });
        return { ...prev, groups };
      });
      toast.success(`Status updated → ${newStatus.replace(/_/g, " ")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    } finally {
      setSavingTaskId(null);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="hybrid-coordination-rollup">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-500" />
            <p className="text-[0.7rem] font-semibold text-slate-500 uppercase tracking-wider">
              Hybrid App Coordination (3-role workstreams)
            </p>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Each group shows IT Function / IT-DRP / App Manager tasks running in parallel for a hybrid-hosted application.
          </p>
        </div>
        <span className="text-2xl font-extrabold text-slate-800">{data.count}</span>
      </div>

      <div className="space-y-3">
        {data.groups.map((g) => (
          <div
            key={g.coordination_group_id}
            className="border border-slate-200 rounded-lg p-3 hover:border-purple-300 transition-colors"
            data-testid={`hybrid-group-${g.coordination_group_id}`}
          >
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  {g.application_name || "Hybrid Application"}
                </p>
                <p className="text-[11px] text-slate-500">
                  for process: <span className="font-medium text-slate-600">{g.process_name}</span>
                  {g.rto_hours != null && <> · RTO {g.rto_hours}h</>}
                  {g.rpo_hours != null && <> · RPO {g.rpo_hours}h</>}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 text-xs">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  <span
                    className="font-semibold text-slate-700"
                    data-testid={`group-completion-${g.coordination_group_id}`}
                  >
                    {g.completion_pct}%
                  </span>
                </div>
                <div className="w-24 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 transition-all"
                    style={{ width: `${g.completion_pct || 0}%` }}
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {(g.tasks || []).map((t) => {
                const Icon = roleIcon(t.assigned_role);
                const editable = canEdit(t);
                const busy = savingTaskId === t.id;
                return (
                  <div
                    key={t.id}
                    className={`border rounded-md p-2 ${roleTone(t.assigned_role)}`}
                    data-testid={`hybrid-task-tile-${t.id}`}
                  >
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold">
                      <Icon className="w-3.5 h-3.5" />
                      {t.assigned_role?.replace(/_/g, " ")}
                      <span className="ml-auto text-[9px] px-1 rounded bg-white/70">{t.status}</span>
                    </div>
                    <p className="text-[11px] mt-1 leading-snug opacity-90 line-clamp-2">
                      {t.description}
                    </p>
                    {editable && (
                      <select
                        value={t.status || "pending"}
                        disabled={busy}
                        onChange={(e) => updateStatus(t, g.coordination_group_id, e.target.value)}
                        className="mt-1.5 w-full text-[10px] rounded border border-white/60 bg-white/90 px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-purple-400"
                        data-testid={`hybrid-task-status-${t.id}`}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s.replace(/_/g, " ")}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
